<template>
  <div>
    <app-spinner v-if="!is_loaded"/>
    <template v-else>
      <app-cells position="between">
        <h1 class="title">Leads</h1>
        <app-button
          v-if="is_general_manager || is_sales_supervisor || is_marketing_supervisor"
          @click.native="onShowExportModal"
          theme="export"
        >
          <img src="@/assets/img/export-icon.svg" alt="Export">
          <span>Export in .xlsx</span>
        </app-button>
      </app-cells>
      <app-cells>
        <app-button
          v-if="is_general_manager || is_sales_manager || is_sales_supervisor"
          @click.native="onShowCreateModal"
        >
          New lead
        </app-button>
        <app-button
          v-if="is_general_manager || is_sales_supervisor || is_support"
          @click.native="onShowCreateDirectModal"
          theme="info"
        >
          New direct lead
        </app-button>
        <app-button v-if="lead_count > 0" @click.native="onAssignLead" theme="warn" ref="assign_lead">
          Assign lead to me ({{ lead_count }} available)
        </app-button>
      </app-cells>
      <template v-if="(is_general_manager || is_sales_supervisor) && table_new.items.length">
        <app-cells><h2 class="title">New</h2></app-cells>
        <v-client-table
          :data="table_new.items"
          :columns="table_new.columns"
          :options="table_new.options"
          class="table-default"
          :class="{ 'table-default--loading': table_new_preloader }"
        >
          <template #h__name><div class="table-default__left">Name</div></template>
          <template #h__timezone><div class="table-default__left">Lead time zone</div></template>
          <template #name="{ row }">
            <div class="table-default__left table-default__controls">
              <LeadManagementGuidanceIcon v-if="row.management_guidance_status" :status="row.management_guidance_status" :comment="row.management_guidance_comment" design="legacy" />
              <span
                class="table-default__activity new-lead-quick-payment"
                :class="{ 'new-lead-quick-payment--active': row.quick_payment }"
                role="img"
                :aria-label="row.quick_payment ? 'Quick payment available' : 'No quick payment'"
              >
                <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path
                    d="M16.5 8.25C15.8 7.2 14.5 6.75 12.5 6.75C10.15 6.75 8.5 7.9 8.5 9.5C8.5 13.5 16.5 11.5 16.5 15.5C16.5 17.1 14.85 18.25 12.5 18.25C10.5 18.25 9.2 17.8 8.5 16.75M12.5 4.75V20.25"
                    stroke="white"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <v-popover
                :disabled="!newLeadRequestDetails(row).length"
                trigger="hover focus"
                placement="right"
                popover-inner-class="tooltip-inner popover-inner new-lead-request-popover-inner"
                :offset="10"
              >
                <span
                  class="table-default__activity table-default__activity--notes"
                  :class="{ 'table-default__activity--active': newLeadRequestDetails(row).length }"
                  style="display: block"
                  role="img"
                  aria-label="Request details"
                ></span>
                <template #popover>
                  <div
                    v-for="detail in newLeadRequestDetails(row)"
                    :key="detail.label"
                    :class="{ 'new-lead-request-text': detail.label === 'Request' }"
                  >
                    <strong>{{ detail.label }}:</strong> {{ detail.value }}
                  </div>
                </template>
              </v-popover>
              <span
                :class="`fflag ff-md fflag-${row.country ? row.country.code : 'empty'}`"
                v-tooltip="{ content: row.country ? row.country.name : 'Not provided', placement: 'top-end', offset: 10 }"
              ></span>
              <router-link :to="{name: 'leads-detail-lead', params: {id: row.id }}" class="table-default__link-border">
                {{ row.name }}
              </router-link>
              <span v-if="row.pax">({{ row.pax }})</span>
              <span v-if="row.tour_operator"
                    v-tooltip="{ content: row.tour_operator.name, placement: 'top-start', offset: 10 }">
              ({{ row.tour_operator.short_name }})
            </span>
            </div>
          </template>
          <template #communication="{ row }">
            <div class="table-default__controls">
              <button
                v-tooltip="{content: row.email, placement: 'top-start', offset: 10}"
                @click="onCopyContactLink(row.email)"
                class="table-default__activity table-default__activity--mail"
                :class="{'table-default__activity--active': row.email}"
              >
              </button>
              <button
                v-tooltip="{content: row.phone, placement: 'top-start', offset: 10}"
                @click="onCopyContactLink(row.phone)"
                class="table-default__activity table-default__activity--phone"
                :class="{'table-default__activity--active': row.phone}"
              >
              </button>
            </div>
          </template>
          <template #last_contact="{ row }">
            {{ dateTimeFormat(row.created_at) }}
          </template>
          <template #timezone="{ row }">
            <div class="table-default__left">{{ row.country_timezone?.timezone_name || '-' }}</div>
          </template>
          <template #activities="{ row }">
            <div class="table-default__controls">
            <span
              class="table-default__activity table-default__activity--climbing table-default__activity--no-events"
              :class="{'table-default__activity--active': row.climbing}"
            />
              <span
                class="table-default__activity table-default__activity--safari table-default__activity--no-events"
                :class="{'table-default__activity--active': row.safari}"
              />
              <span
                class="table-default__activity table-default__activity--day_trips table-default__activity--no-events"
                :class="{'table-default__activity--active': row.day_trip}"
              />
              <span
                class="table-default__activity table-default__activity--island table-default__activity--no-events"
                :class="{'table-default__activity--active': row.island}"
              />
            </div>
          </template>
          <template #travel_time="{ row }">
            {{ dateFormatMonth(row.travel_time) }}
          </template>
          <template #countdown="{ row }">
            <span
              class="status-tag"
              :style="{ 'backgroundColor': calculateTimeDiff(row.created_at).difference < 10 ? '#77b017' : calculateTimeDiff(row.created_at).difference < 30 ? '#f0c427' : '#e85c20' }"
            >
              {{ calculateTimeDiff(row.created_at).time_string }}
            </span>
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationNew"
              v-if="pagination_new.pages > 1"
              v-model="pagination_new.page"
              :page-count="pagination_new.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_new.count" :count="table_new.items.length" :page="pagination_new.page"
                             :limit="10"/>
        </app-cells>
      </template>
      <template v-if="table_new_double.items.length">
        <app-cells><h2 class="title">New double request</h2></app-cells>
        <v-client-table
          :data="table_new_double.items"
          :columns="table_new_double.columns"
          :options="table_new_double.options"
          class="table-default"
          :class="{ 'table-default--loading': table_new_double_preloader }"
        >
          <template #h__name>
            <div class="table-default__left">Name</div>
          </template>
          <template #name="{ row }">
            <div class="table-default__left table-default__controls">
              <LeadManagementGuidanceIcon v-if="row.management_guidance_status" :status="row.management_guidance_status" :comment="row.management_guidance_comment" design="legacy" />
              <span
                :class="`fflag ff-md fflag-${row.country ? row.country.code : 'empty'}`"
                v-tooltip="{ content: row.country ? row.country.name : 'Not provided', placement: 'top-end', offset: 10 }"
              ></span>
              <router-link :to="{name: 'leads-detail-lead', params: {id: row.id }}" class="table-default__link-border">
                {{ row.name }}
              </router-link>
              <span v-if="row.pax">({{ row.pax }})</span>
              <span v-if="row.tour_operator"
                    v-tooltip="{ content: row.tour_operator.name, placement: 'top-start', offset: 10 }">
              ({{ row.tour_operator.short_name }})
            </span>
            </div>
          </template>
          <template #communication="{ row }">
            <div class="table-default__controls">
              <button
                v-tooltip="{content: row.email, placement: 'top-start', offset: 10}"
                @click="onCopyContactLink(row.email)"
                class="table-default__activity table-default__activity--mail"
                :class="{'table-default__activity--active': row.email}"
              >
              </button>
              <button
                v-tooltip="{content: row.phone, placement: 'top-start', offset: 10}"
                @click="onCopyContactLink(row.phone)"
                class="table-default__activity table-default__activity--phone"
                :class="{'table-default__activity--active': row.phone}"
              >
              </button>
            </div>
          </template>
          <template #last_contact="{ row }">
            {{ dateTimeFormat(row.created_at) }}
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationNewDouble"
              v-if="pagination_new_double.pages > 1"
              v-model="pagination_new_double.page"
              :page-count="pagination_new_double.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_new_double.count" :count="table_new_double.items.length"
                             :page="pagination_new_double.page" :limit="10"/>
        </app-cells>
      </template>
      <app-cells>
        <h2 class="title">Assigned</h2>
        <app-input
          @input.native="onLeadsSearch"
          v-model="search"
          class="input--300"
          type="search"
          placeholder="Search by name, phone, email"
          name="search"
        />
        <VueCtkDateTimePicker
          id="date_picker_leads_request"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates_request"
          :custom-shortcuts="custom_shortcuts_request"
          @is-hidden="fetchItemsRequest"
          @input="onInputPickerRequest"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Request date"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          ref="date_picker_request"
        />
        <VueCtkDateTimePicker
          id="date_picker_leads_confiramation"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates_confirmation"
          :custom-shortcuts="custom_shortcuts_request"
          @is-hidden="fetchItemsRequest"
          @input="onInputPickerConfirmation"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Confirmation date"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          ref="date_picker_confirmation"
        />
        <app-select
          v-if="is_general_manager || is_sales_supervisor || is_support || is_marketing_supervisor"
          @input="onChangeFilter('manager_select')"
          v-model="manager"
          :options="managers_options"
          :reduce="item => item.id"
          :display-field="option => `${option.first_name} ${option.last_name}`"
          label="first_name"
          placeholder="Sales manager"
          class="select--300"
        />
        <!--        <app-filters-->
        <!--          v-model="status_filter"-->
        <!--          :filters="status_filters"-->
        <!--          name="filterStatus"-->
        <!--          @change="onChangeFilter"-->
        <!--        />-->
      </app-cells>
      <app-cells>
        <VueCtkDateTimePicker
          id="date_picker_leads"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates"
          :custom-shortcuts="custom_shortcuts"
          @is-hidden="fetchItems"
          @input="onInputPicker"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Travel dates"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          ref="date_picker"
        />
        <app-checkbox @change="onChangeFilter" v-model="filter_climbing" label="Climbing"/>
        <app-checkbox @change="onChangeFilter" v-model="filter_safari" label="Safari"/>
        <app-checkbox @change="onChangeFilter" v-model="filter_day_trip" label="Day trip"/>
        <app-checkbox @change="onChangeFilter" v-model="filter_island" label="Island"/>
        <app-select
          @input="onChangeFilter('manager_select')"
          v-model="language_filter"
          :options="languages_options"
          :reduce="item => item.code"
          placeholder="Language"
          class="select--200"
        />
        <app-select
          v-if="is_general_manager || is_sales_supervisor || is_support || is_marketing_supervisor"
          @input="onInputAdvertisingSourceFilter"
          v-model="adv_source_filter"
          :options="advertising_source_options"
          placeholder="Advertising source"
          class="select--300"
        />
        <app-select
          v-if="adv_campaign_options_filter.length"
          @input="fetchItemsRequest"
          v-model="adv_campaign_filter"
          :options="adv_campaign_options_filter"
          :reduce="item => item.id"
          placeholder="Advertising campaign"
          class="select--300"
        />
        <app-select
          @input="onChangeFilter"
          v-model="tour_operator_filter"
          :options="tour_operators_options"
          :reduce="item => item.id"
          :closeOnSelect="false"
          multiple
          placeholder="Tour operator"
          class="select--300"
        />
      </app-cells>
      <app-cells>
        <div class="filters">
          <label
            v-for="filter of status_filters6"
            :key="filter.value"
            class="filters__label"
            v-show="filter.show"
          >
            <input
              v-model="status_filter"
              @change="onChangeFilter"
              type="radio"
              name="filterStatus"
              :value="filter.value"
            >
            <span class="filters__item">{{ filter.label }}</span>
          </label>
        </div>
        <!--        <app-filters-->
        <!--          v-model="status_filter"-->
        <!--          :filters="status_filters6"-->
        <!--          name="filterStatus"-->
        <!--          @change="onChangeFilter"-->
        <!--        />-->
        <app-filters
          v-model="status_filter"
          :filters="status_filters1"
          name="filterStatus"
          @change="onChangeFilter"
        />
        <app-filters
          v-model="status_filter"
          :filters="status_filters3"
          name="filterStatus"
          @change="onChangeFilter"
        />
      </app-cells>
      <app-cells>
        <app-filters
          v-model="status_filter"
          :filters="status_filters2"
          name="filterStatus"
          @change="onChangeFilter"
        />
        <app-filters
          v-model="status_filter"
          :filters="status_filters4"
          name="filterStatus"
          @change="onChangeFilter"
        />
        <app-filters
          v-model="status_filter"
          :filters="status_filters5"
          name="filterStatus"
          @change="onChangeFilter"
        />
      </app-cells>
      <app-cells>
        <app-checkbox v-if="is_sales_manager" @change="onChangeFilter" v-model="filter_my_lead" label="My leads"/>
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__status>
          <div class="table-default__left">Status</div>
        </template>
        <template #h__name>
          <div class="table-default__left">Name</div>
        </template>
        <template #status="{ row }">
          <div v-if="is_sales_manager && !row.my_lead"
               class="table-default__photo table-default__photo--absolute table-default__photo--opacity">
            <img
              :src="getUserAvatar(row.manager.crop.crop_url)" alt="Avatar"
              v-tooltip="{
                content: `${row.manager.full_name}${row.manager.vacation ? `<br>Vacation: ${dateFormat(row.manager.vacation.from_date)} - ${dateFormat(row.manager.vacation.to_date)}` : ''}`,
                placement: 'top',
                offset: 10,
                html: true
              }"
            >
          </div>
          <div class="table-default__left">
            <app-option-picker
              v-model="row.status"
              :options="row.status.available_statuses"
              @click="onOptionSelected(row)"
              :disable="!is_general_manager && !is_sales_supervisor && !is_sales_manager"
            />
          </div>
        </template>
        <template #name="{ row }">
          <div class="table-default__left table-default__controls">
            <LeadManagementGuidanceIcon v-if="row.management_guidance_status" :status="row.management_guidance_status" :comment="row.management_guidance_comment" design="legacy" />
            <span
              v-tooltip="{ content: row.note, placement: 'top-end', offset: 10 }"
              class="table-default__activity table-default__activity--notes"
              :class="{'table-default__activity--active': row.note}"
            ></span>
            <span
              :class="`fflag ff-md fflag-${row.country ? row.country.code : 'empty'}`"
              v-tooltip="{ content: row.country ? row.country.name : 'Not provided', placement: 'top-start', offset: 10 }"
            ></span>
            <router-link :to="{name: 'leads-detail-lead', params: {id: row.id }}" class="table-default__link-border">
              {{ row.name }}
            </router-link>
            <span v-if="row.pax">({{ row.pax }})</span>
            <span v-if="row.tour_operator"
                  v-tooltip="{ content: row.tour_operator.name, placement: 'top-start', offset: 10 }">
              ({{ row.tour_operator.short_name }})
            </span>
            <span v-if="row.is_double_request" class="status-tag" style="background-color: #439ad1">DR</span>
          </div>
        </template>
        <template #communication="{ row }">
          <div class="table-default__controls">
            <button
              v-tooltip="{content: row.email, placement: 'top-start', offset: 10}"
              @click="onCopyContactLink(row.email)"
              class="table-default__activity table-default__activity--mail"
              :class="{'table-default__activity--active': row.email}"
            />
            <button
              v-tooltip="{content: row.phone, placement: 'top-start', offset: 10}"
              @click="onCopyContactLink(row.phone)"
              class="table-default__activity table-default__activity--phone"
              :class="{'table-default__activity--active': row.phone}"
            />
            <a
              :href="`https://t.me/${row.phone}`"
              target="_blank"
              class="table-default__activity table-default__activity--tg"
              :class="{'table-default__activity--active': row.phone}"
            />
            <a
              :href="`https://wa.me/${row.phone}`"
              target="_blank"
              class="table-default__activity table-default__activity--wa"
              :class="{'table-default__activity--active': row.phone}"
            />
            <a
              :href="row.facebook"
              target="_blank"
              class="table-default__activity table-default__activity--facebook"
              :class="{'table-default__activity--active': row.facebook}"
            />
            <a
              :href="row.instagram"
              target="_blank"
              class="table-default__activity table-default__activity--instagram"
              :class="{'table-default__activity--active': row.instagram}"
            />
          </div>
        </template>
        <template #last_contact="{ row }">
          <div class="table-default__controls">
            <template v-if="is_general_manager || is_sales_supervisor || is_sales_manager">
              <button
                v-if="row.last_contact"
                @click="onShowDateModal(row.id)"
                v-tooltip="{content: `Request date: ${dateFormatLastContact(row.created_at)}`, placement: 'top-start', offset: 10}"
                class="table-default__controls-btn"
              >
                <span v-if="row.last_contact_expired" class="status-tag" :style="{ 'backgroundColor': '#e85c20' }">
                  {{ dateFormat(row.last_contact) }}
                </span>
                <span v-else>{{ dateFormat(row.last_contact) }}</span>
              </button>
              <span
                v-else
                v-tooltip="{content: `Request date: ${dateFormatLastContact(row.created_at)}`, placement: 'top-start', offset: 10}"
                class="status-tag"
                :style="{ 'backgroundColor': '#f0c427' }"
              >
                No contact
              </span>
              <button
                @click="onShowFollowModal(row)"
                class="table-default__activity table-default__activity--fl"
                :class="{'table-default__activity--active': row.email && row.last_contact_expired}"
              />
            </template>
            <template v-else>
              <template v-if="row.last_contact">
                <span v-if="row.last_contact_expired" class="status-tag" :style="{ 'backgroundColor': '#e85c20' }">
                  {{ dateFormat(row.last_contact) }}
                </span>
                <span v-else>{{ dateFormat(row.last_contact) }}</span>
              </template>
              <span
                v-else
                v-tooltip="{content: `Request date: ${dateFormatLastContact(row.created_at)}`, placement: 'top-start', offset: 10}"
                class="status-tag"
                :style="{ 'backgroundColor': '#f0c427' }"
              >
                No contact
              </span>
            </template>
          </div>
        </template>
        <template #activities="{ row }">
          <div class="table-default__controls">
            <span
              class="table-default__activity table-default__activity--climbing table-default__activity--no-events"
              :class="{'table-default__activity--active': row.climbing}"
            />
            <span
              class="table-default__activity table-default__activity--safari table-default__activity--no-events"
              :class="{'table-default__activity--active': row.safari}"
            />
            <span
              class="table-default__activity table-default__activity--day_trips table-default__activity--no-events"
              :class="{'table-default__activity--active': row.day_trip}"
            />
            <span
              class="table-default__activity table-default__activity--island table-default__activity--no-events"
              :class="{'table-default__activity--active': row.island}"
            />
          </div>
        </template>
        <template #travel_time="{ row }">
          {{ dateFormatMonth(row.travel_time) }}
        </template>
        <template #itinerary="{ row }">
          <span
            v-tooltip="{ content: `${row.itinerary?.amount ? `${row.itinerary.amount.toLocaleString()} USD` : ''}`, placement: 'top-start', offset: 10 }">
            {{ row.itinerary?.itinerary_amount ? row.itinerary.itinerary_amount.toLocaleString() : '-'
            }} {{ row.itinerary?.itinerary_currency?.code }}
          </span>
        </template>
        <template #commission="{ row }">
          {{ row.itinerary?.commission ? `${ row.itinerary?.commission.toLocaleString() } USD` : '-' }}
        </template>
        <template #manager="{ row }">
          <div class="table-default__controls">
            <div v-if="row.manager" class="table-default__photo">
              <img
                :src="getUserAvatar(row.manager.crop.crop_url)" alt="Avatar"
                v-tooltip="{ content: row.manager.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            <span v-else>-</span>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page"/>
      </app-cells>
    </template>
    <modal
      name="leads-modal"
      :width="700"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideCreateModal" class="popup__close" type="button"></button>
        <div class="popup__title">
          <span>Create lead</span>
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-grid>
              <template #item-1>
                <app-form-group label="Status" required>
                  <app-select
                    v-model="form.status"
                    :reduce="item => item.code"
                    :options="status_options"
                    :clearable="false"
                    placeholder="Status"
                    :error="$v.form.status.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.status.$dirty && !$v.form.status.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Source" required>
                  <app-select
                    @input="onLeadSourceChanged"
                    v-model="form.source"
                    :reduce="item => item.code"
                    :options="source_options"
                    :clearable="false"
                    placeholder="Source"
                    :error="$v.form.source.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.source.$dirty && !$v.form.source.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group v-if="form.source && (form.source === 'jivosite' || form.source === 'whatsapp')"
                                label="Ticket ID" required>
                  <app-select
                    v-model="form.trengo_ticket"
                    @search="onTicketSearch"
                    :options="trengo_ticket_options"
                    :clearable="false"
                    label="ticket_id"
                    :display-field="option => `Ticket #${option.ticket_id}`"
                    placeholder="Ticket ID"
                    :error="$v.form.trengo_ticket.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.trengo_ticket.$dirty && !$v.form.trengo_ticket.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Name" label-for="lead_name" required>
                  <app-input
                    v-model="form.name"
                    :error="$v.form.name.$error"
                    id="lead_name"
                    placeholder="Name"
                    name="lead_name"
                  />
                  <template #error>
                    <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Language" required>
                  <app-select
                    v-model="form.language"
                    :reduce="item => item.code"
                    :options="languages_options"
                    :filterable="false"
                    :clearable="false"
                    :searchable="false"
                    placeholder="Language"
                  />
                  <template #error>
                    <div v-if="$v.form.language.$dirty && !$v.form.language.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Pax" label-for="lead_pax">
                  <app-input
                    v-model.number="form.pax"
                    type="number"
                    min="0"
                    id="lead_pax"
                    placeholder="Pax"
                    name="lead_pax"
                  />
                </app-form-group>
                <app-form-group label="Tour operator">
                  <app-select
                    v-model="form.tour_operator"
                    :reduce="item => item.id"
                    :options="tour_operators_options"
                    placeholder="Tour operator"
                  />
                </app-form-group>
                <app-form-group label="Country">
                  <app-select
                    v-model="form.country"
                    @input="onCountryChange('form')"
                    :reduce="item => item.code"
                    :options="country_options"
                    placeholder="Country"
                    class="select"
                  />
                </app-form-group>
                <app-form-group label="Time zone" :disabled="!form.country">
                  <app-select
                    v-model="form.country_timezone"
                    :reduce="item => item.id"
                    :options="timezone_options"
                    :disabled="!form.country"
                    :display-field="timezoneLabel"
                    label="label"
                    placeholder="Time zone"
                  />
                </app-form-group>
                <app-form-group label="Travel time">
                  <app-date-picker
                    v-model="form.travel_time"
                    format="MMM YYYY"
                    type="month"
                    placeholder="Travel time"
                  />
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Activities" class="form-group--mh">
                  <app-cells :indent="false">
                    <app-checkbox-pic v-model="form.climbing" activeColor="#219b52" picPath="climbing-table-icon.svg"/>
                    <app-checkbox-pic v-model="form.safari" activeColor="#eb9514" picPath="safari-table-icon.svg"/>
                    <app-checkbox-pic v-model="form.day_trip" activeColor="#77b017" picPath="day_trips-table-icon.svg"/>
                    <app-checkbox-pic v-model="form.island" activeColor="#da3e63" picPath="island-table-icon.svg"/>
                  </app-cells>
                </app-form-group>
                <app-form-group label="E-mail" label-for="email">
                  <app-input
                    v-model="form.email"
                    @change.native="$v.form.email.$touch"
                    :error="$v.form.email.$error"
                    id="email"
                    type="email"
                    placeholder="E-mail"
                    name="email"
                  />
                  <template #error>
                    <div v-if="$v.form.email.$dirty && !$v.form.email.email">Enter the correct e-mail format</div>
                  </template>
                </app-form-group>
                <app-form-group label="Phone">
                  <app-phone
                    @change.native="$v.form.phone.$touch()"
                    v-model="form.phone"
                    placeholder="+255 786 350 350"
                    :error="$v.form.phone.$dirty && form.phone === 0"
                  />
                  <template #error>
                    <div v-if="$v.form.phone.$dirty && form.phone === 0">
                      Invalid phone number format
                    </div>
                  </template>
                </app-form-group>
                <app-form-group label="Facebook" label-for="facebook">
                  <app-input
                    v-model="form.facebook"
                    :error="$v.form.facebook.$error"
                    id="facebook"
                    placeholder="Facebook"
                    name="facebook"
                  />
                  <template #error>
                    <div v-if="$v.form.facebook.$invalid">Wrong link</div>
                  </template>
                </app-form-group>
                <app-form-group label="Instagram" label-for="instagram">
                  <app-input
                    v-model="form.instagram"
                    :error="$v.form.instagram.$error"
                    id="facebook"
                    placeholder="Instagram"
                    name="instagram"
                  />
                  <template #error>
                    <div v-if="$v.form.instagram.$invalid">Wrong link</div>
                  </template>
                </app-form-group>
              </template>
            </app-grid>
            <app-form-group label="Notes" label-for="notes">
              <app-textarea
                v-model="form.note"
                id="notes"
                placeholder="Notes"
              />
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button type="submit" ref="submit">Create</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="reason-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('reason-modal')" class="popup__close" type="button"></button>
        <div class="popup__title"><span>Choose reason</span></div>
        <div class="popup__body">
          <form @submit.prevent="onCheckReasonForm">
            <app-form-group label="Reason" required>
              <app-select
                v-model="reason_form.status_reason"
                :options="reason_options"
                :reduce="item => item.code"
                :clearable="false"
                placeholder="Reason"
                :error="$v.reason_form.status_reason.$error"
              />
              <template #error>
                <div v-if="$v.reason_form.status_reason.$dirty && !$v.reason_form.status_reason.required">Required
                  field
                </div>
              </template>
            </app-form-group>
            <app-form-group label="Additional notes" label-for="reason-notes">
              <app-textarea
                v-model="reason_form.status_reason_note"
                id="reason-notes"
                placeholder="Additional notes"
              />
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button type="submit" ref="reason_submit" :disabled="$v.reason_form.$error">Save status</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="date-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('date-modal')" class="popup__close" type="button"></button>
        <div class="popup__title"><span>Report communication</span></div>
        <div class="popup__body">
          <form @submit.prevent="onCheckDateForm">
            <app-form-group label="Date" required>
              <app-date-picker
                v-model="date_form.date"
                format="DD MMM YYYY HH:mm"
                value-type="YYYY-MM-DDTHH:mm:ssZ"
                type="datetime"
                placeholder="Date"
              />
              <template #error>
                <div v-if="$v.date_form.date.$dirty && !$v.date_form.date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Communication method" required>
              <app-select
                v-model="date_form.methods"
                :options="communication_options"
                :clearable="false"
                multiple
                placeholder="Communication method"
                :error="$v.date_form.methods.$error"
              />
              <template #error>
                <div v-if="$v.date_form.methods.$dirty && !$v.date_form.methods.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Report" label-for="date-notes" :required="report_required">
              <app-textarea
                v-model="date_form.report"
                id="date-notes"
                placeholder="Report"
                :error="$v.date_form.report.$error"
              />
              <template #error>
                <div v-if="$v.date_form.report.$dirty && !$v.date_form.report.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button type="submit" ref="date_submit" :disabled="$v.date_form.$error">Save</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="follow-modal"
      :width="450"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('follow-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Choose follow up to send</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFollowForm">
            <app-form-group>
              <app-cells :indent="false">
                <app-checkbox @change="onChangeFilterFollow" v-model="follow_filters.climbing" label="Climbing"/>
                <app-checkbox @change="onChangeFilterFollow" v-model="follow_filters.safari" label="Safari"/>
                <app-checkbox @change="onChangeFilterFollow" v-model="follow_filters.day_trip" label="Day trip"/>
                <app-checkbox @change="onChangeFilterFollow" v-model="follow_filters.island" label="Island"/>
              </app-cells>
            </app-form-group>
            <app-form-group label="Template" required>
              <app-select
                v-model="follow_form.template"
                :options="templates_options"
                :reduce="item => item.id"
                :clearable="false"
                :get-option-label="item => item.template.name"
                :display-field="option => option.template.name"
                placeholder="Template"
                :error="$v.follow_form.template.$error"
              />
              <template #error>
                <div v-if="$v.follow_form.template.$dirty && !$v.follow_form.template.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="follow_submit">Send</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="export-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('export-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Lead export settings</div>
        <div class="popup__body">
          <form @submit.prevent="onSendExport">
            <app-form-group label="Last contact">
              <app-date-picker
                v-model="export_form.last_contact"
                range
                placeholder="Last contact"
              />
            </app-form-group>
            <app-form-group label="Date of request">
              <app-date-picker
                v-model="export_form.created_at"
                range
                placeholder="Date of request"
              />
            </app-form-group>
            <app-form-group label="Activities" class="form-group--mh">
              <app-cells :indent="false">
                <app-checkbox-pic
                  v-model="export_form.climbing"
                  activeColor="#219b52"
                  picPath="climbing-table-icon.svg"
                />
                <app-checkbox-pic
                  v-model="export_form.safari"
                  activeColor="#eb9514"
                  picPath="safari-table-icon.svg"
                />
                <app-checkbox-pic
                  v-model="export_form.day_trip"
                  activeColor="#77b017"
                  picPath="day_trips-table-icon.svg"
                />
                <app-checkbox-pic
                  v-model="export_form.island"
                  activeColor="#da3e63"
                  picPath="island-table-icon.svg"
                />
              </app-cells>
            </app-form-group>
            <app-form-group label="Status">
              <app-select
                v-model="export_form.status"
                :options="status_options"
                :reduce="item => item.code"
                multiple
                placeholder="Status"
              />
            </app-form-group>
            <app-form-group label="Status exclude">
              <app-select
                v-model="export_form.status_exclude"
                :options="status_options"
                :reduce="item => item.code"
                multiple
                placeholder="Status"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="export_submit">Export</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="next-contact-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('next-contact-modal')" class="popup__close" type="button"></button>
        <div class="popup__title"><span>Set next contact</span></div>
        <div class="popup__body">
          <form @submit.prevent="onCheckNextContactForm">
            <app-form-group label="Next contact" required>
              <app-date-picker
                v-model="next_contact_form.next_contact"
                placeholder="Next contact"
                :error="$v.next_contact_form.next_contact.$error"
              />
              <template #error>
                <div v-if="$v.next_contact_form.next_contact.$dirty && !$v.next_contact_form.next_contact.required">
                  Required field
                </div>
                <div v-if="$v.next_contact_form.next_contact.$dirty && !$v.next_contact_form.next_contact.minValue">
                  Date cannot be less than the current date
                </div>
              </template>
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button type="submit" ref="next_contact_submit" :disabled="$v.next_contact_form.$error">Save
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="leads-direct-modal"
      :width="700"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideCreateDirectModal" class="popup__close" type="button"></button>
        <div class="popup__title">
          <span>Create lead</span>
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFormDirect">
            <app-grid>
              <template #item-1>
                <app-form-group label="Date of request" required>
                  <app-date-picker
                    v-model="direct_form.created_at"
                    format="DD MMM YYYY HH:mm"
                    value-type="YYYY-MM-DDTHH:mm"
                    type="datetime"
                    placeholder="Date of request"
                    :error="$v.direct_form.created_at.$error"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.created_at.$dirty && !$v.direct_form.created_at.required">Required field
                    </div>
                  </template>
                </app-form-group>
                <app-form-group label="Source" required>
                  <app-select
                    v-model="direct_form.source"
                    :options="source_options"
                    :reduce="item => item.code"
                    :clearable="false"
                    placeholder="Source"
                    :error="$v.direct_form.source.$error"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.source.$dirty && !$v.direct_form.source.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Name" label-for="lead_name" required>
                  <app-input
                    v-model="direct_form.name"
                    :error="$v.direct_form.name.$error"
                    id="lead_name"
                    placeholder="Name"
                    name="lead_name"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.name.$dirty && !$v.direct_form.name.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Language" required>
                  <app-select
                    v-model="direct_form.language"
                    :options="languages_options"
                    :reduce="item => item.code"
                    :filterable="false"
                    :clearable="false"
                    :searchable="false"
                    placeholder="Language"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.language.$dirty && !$v.direct_form.language.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Pax" label-for="lead_direct_pax">
                  <app-input
                    v-model.number="direct_form.pax"
                    type="number"
                    min="0"
                    id="lead_direct_pax"
                    placeholder="Pax"
                    name="lead_direct_pax"
                  />
                </app-form-group>
                <app-form-group label="Tour operator">
                  <app-select
                    v-model="direct_form.tour_operator"
                    :options="tour_operators_options"
                    :reduce="item => item.id"
                    placeholder="Tour operator"
                  />
                </app-form-group>
                <app-form-group label="Country">
                  <app-select
                    v-model="direct_form.country"
                    @input="onCountryChange('direct_form')"
                    :options="country_options"
                    :reduce="item => item.code"
                    placeholder="Country"
                  />
                </app-form-group>
                <app-form-group label="Time zone" :disabled="!direct_form.country">
                  <app-select
                    v-model="direct_form.country_timezone"
                    :reduce="item => item.id"
                    :options="direct_timezone_options"
                    :disabled="!direct_form.country"
                    :display-field="timezoneLabel"
                    label="label"
                    placeholder="Time zone"
                  />
                </app-form-group>
                <app-form-group label="Travel time">
                  <app-date-picker
                    v-model="direct_form.travel_time"
                    format="MMM YYYY"
                    placeholder="Travel time"
                    type="month"
                  />
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Activities" class="form-group--mh">
                  <app-cells :indent="false">
                    <app-checkbox-pic v-model="direct_form.climbing" activeColor="#219b52"
                                      picPath="climbing-table-icon.svg"/>
                    <app-checkbox-pic v-model="direct_form.safari" activeColor="#eb9514"
                                      picPath="safari-table-icon.svg"/>
                    <app-checkbox-pic v-model="direct_form.day_trip" activeColor="#77b017"
                                      picPath="day_trips-table-icon.svg"/>
                    <app-checkbox-pic v-model="direct_form.island" activeColor="#da3e63"
                                      picPath="island-table-icon.svg"/>
                  </app-cells>
                </app-form-group>
                <app-form-group label="Preferred communication method" required>
                  <app-select
                    v-model="direct_form.preferred_communication_method"
                    :options="[{ code: 'whatsapp', name: 'WhatsApp' }, { code: 'email', name: 'Email' }]"
                    :reduce="item => item.code"
                    :clearable="false"
                    :searchable="false"
                    :error="$v.direct_form.preferred_communication_method.$error"
                    placeholder="Select communication method"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.preferred_communication_method.$error">Select WhatsApp or Email</div>
                  </template>
                </app-form-group>
                <app-form-group label="E-mail" label-for="direct-email" :required="direct_form.preferred_communication_method === 'email'">
                  <app-input
                    v-model="direct_form.email"
                    @change.native="$v.direct_form.email.$touch"
                    :error="$v.direct_form.email.$error"
                    id="direct-email"
                    type="email"
                    placeholder="E-mail"
                    name="direct-email"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.email.$dirty && !$v.direct_form.email.required">Email is required for this method</div>
                    <div v-if="$v.direct_form.email.$dirty && !$v.direct_form.email.email">Enter the correct e-mail
                      format
                    </div>
                  </template>
                </app-form-group>
                <app-form-group label="Phone" :required="direct_form.preferred_communication_method === 'whatsapp'">
                  <app-phone
                    @change.native="$v.direct_form.phone.$touch()"
                    v-model="direct_form.phone"
                    placeholder="+255 786 350 350"
                    :error="$v.direct_form.phone.$error"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.phone.$dirty && !$v.direct_form.phone.required">Phone is required for WhatsApp</div>
                    <div v-if="$v.direct_form.phone.$dirty && direct_form.phone === 0">
                      Invalid phone number format
                    </div>
                  </template>
                </app-form-group>
                <app-form-group label="Facebook" label-for="direct-facebook">
                  <app-input
                    v-model="direct_form.facebook"
                    :error="$v.direct_form.facebook.$error"
                    id="direct-facebook"
                    placeholder="Facebook"
                    name="direct-facebook"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.facebook.$invalid">Wrong link</div>
                  </template>
                </app-form-group>
                <app-form-group label="Instagram" label-for="direct-instagram">
                  <app-input
                    v-model="direct_form.instagram"
                    :error="$v.direct_form.instagram.$error"
                    id="direct-instagram"
                    placeholder="Instagram"
                    name="direct-instagram"
                  />
                  <template #error>
                    <div v-if="$v.direct_form.instagram.$invalid">Wrong link</div>
                  </template>
                </app-form-group>
              </template>
            </app-grid>
            <app-form-group label="Request" label-for="direct_notes" required>
              <app-textarea
                v-model="direct_form.direct_request_info"
                id="direct_notes"
                placeholder="Request"
                :error="$v.direct_form.direct_request_info.$error"
              />
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button type="submit" ref="submit_direct">Create</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getClientsTourOperators,
  getCountries,
  getCountryTimezones,
  getLanguages,
  getLeadExport,
  getLeadFollowUpTemplates,
  getLeads,
  getLeadsAdvSources,
  getLeadsAnalytics,
  getLeadsCommunicationMethods,
  getLeadsSources,
  getLeadsStatuses,
  getLeadsStatusReason,
  getLeadsTrengoTickets, getLeadsUnassigned,
  getUsers,
  patchLead,
  postDirectLead,
  postLead,
  postLeadCommunication,
  postLeadFollowUp,
  postLeadsAssigned,
} from '@/http'
import { email, required, requiredIf, url } from 'vuelidate/lib/validators'
import { differenceInMinutes, format, formatISO, parse, parseISO } from 'date-fns'
import debounce from 'lodash/debounce'
import AppCheckboxPic from '@/components/AppCheckboxPic'
import AppOptionPicker from '@/components/AppOptionPicker'
import LeadManagementGuidanceIcon from '@/views/crm/task-tracker/LeadManagementGuidanceIcon.vue'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import moment from 'moment'
import managementGuidancePolling from '@/views/crm/task-tracker/managementGuidancePolling'

export default {
  name: 'LeadsList',
  mixins: [managementGuidancePolling],
  components: { AppCheckboxPic, AppOptionPicker, VueCtkDateTimePicker, LeadManagementGuidanceIcon },
  data() {
    return {
      is_loaded: false,
      table_preloader: false,
      table_new_preloader: false,
      table_new_double_preloader: false,
      search: '',
      filter_climbing: false,
      filter_safari: false,
      filter_day_trip: false,
      filter_island: false,
      filter_my_lead: true,
      filter_dates: null,
      filter_dates_request: null,
      filter_dates_confirmation: null,
      language_filter: null,
      status_filter: 'follow_up',
      status_filters1: [
        { value: 'lead', label: 'Lead' },
        { value: 'negotiation', label: 'Negotiation' },
        { value: 'nurture', label: 'Nurture' },
      ],
      status_filters2: [
        { value: 'pro_forma', label: 'Pro forma' },
        { value: 'deposit_paid', label: 'Deposit paid' },
        { value: 'full_paid', label: 'Full paid' },
      ],
      status_filters3: [
        { value: 'nr1', label: 'NR 1' },
        { value: 'nr2', label: 'NR 2' },
      ],
      status_filters4: [
        { value: 'postponed', label: 'Postponed' },
        { value: 'completed', label: 'Completed' },
      ],
      status_filters5: [
        { value: 'refused', label: 'Refused' },
        { value: 'lost', label: 'Lost' },
        { value: '', label: 'All' },
      ],
      status_filters6: [
        { value: 'follow_up', label: 'Need follow up', show: true },
        { value: 'no_contact', label: 'No contact', show: true },
        { value: 'double_request', label: 'Double request', show: true },
      ],
      adv_source_filter: null,
      adv_campaign_filter: null,
      adv_campaign_options_filter: [],
      tour_operator_filter: null,
      form: {
        adv_campaign: null,
        status: '',
      },
      direct_form: {},
      status_options: [],
      source_options: [],
      country_options: [],
      timezone_options: [],
      direct_timezone_options: [],
      country_cleared: {
        form: false,
        direct_form: false,
      },
      languages_options: [],
      tour_operators_options: [],
      advertising_source_options: [],
      advertising_campaign_options: [],
      custom_shortcuts: [
        {
          key: 'next30Days',
          label: 'Next 30 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(30, 'days')
            }
          }
        },
        {
          key: 'next60Days',
          label: 'Next 60 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(60, 'days')
            }
          }
        },
        {
          key: 'next90Days',
          label: 'Next 90 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(90, 'days')
            }
          }
        },
        { key: 'last30Days', label: 'Last 30 days', value: 30 },
        { key: 'thisYear', label: 'This year', value: 'year' },
      ],
      custom_shortcuts_request: [
        { key: 'last7Days', label: 'Last 7 days', value: 7 },
        { key: 'last30Days', label: 'Last 30 days', value: 30 },
        { key: 'last60Days', label: 'Last 60 days', value: 60 },
        { key: 'last90Days', label: 'Last 90 days', value: 90 },
        { key: 'last180Days', label: 'Last 180 days', value: 180 },
        { key: 'thisYear', label: 'This year', value: 'year' },
        { key: 'lastYear', label: 'Last year', value: '-year' },
      ],
      table: {
        items: [],
        columns: [
          'status',
          'name',
          'communication',
          'last_contact',
          'activities',
          'travel_time',
          'itinerary',
          'commission',
        ],
        options: {
          headings: {
            'status': 'Status',
            'name': 'Name',
            'communication': 'Communication',
            'last_contact': 'Last contact',
            'activities': 'Activities',
            'travel_time': 'Travel time',
            'manager': 'Manager',
            'itinerary': 'Amount',
            'commission': 'Commission',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_new: {
        items: [],
        columns: ['name', 'communication', 'last_contact', 'timezone', 'activities', 'travel_time', 'countdown'],
        options: {
          headings: {
            'name': 'Name',
            'communication': 'Communication',
            'last_contact': 'Request date',
            'timezone': 'Lead time zone',
            'activities': 'Activities',
            'travel_time': 'Travel time',
            'countdown': 'Countdown',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 10,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_new: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_new_double: {
        items: [],
        columns: ['name', 'communication', 'last_contact'],
        options: {
          headings: {
            'name': 'Name',
            'communication': 'Communication',
            'last_contact': 'Request date',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 10,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_new_double: {
        pages: 1,
        page: 1,
        count: 1,
      },
      managers_options: [],
      manager: '',
      analytics_data: {},
      reason_options: [],
      reason_form: {
        status_reason: null,
      },
      next_contact_form: {
        next_contact: null,
      },
      reason_row_updated: null,
      date_form: {
        methods: [],
        report: null,
        date: formatISO(new Date()),
      },
      communication_options: [],
      date_row_id: null,
      follow_form: {
        template: null
      },
      templates_options: [],
      follow_filters: {},
      follow_id: null,
      follow_language: null,
      follow_status: null,
      export_form: {},
      trengo_ticket_options: [],
      lead_count: 0,
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_support() {
      return this.$store.state.userGroups.map(item => item.code).includes('support')
    },
    is_marketing_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('marketing_supervisor')
    },
    report_required() {
      if (!this.date_form.methods.length) return false
      return this.date_form.methods.some(item => item.report_required)
    },
  },
  validations: {
    form: {
      status: { required },
      source: { required },
      trengo_ticket: {
        required: requiredIf(function () {
          return this.form.source && (
            this.form.source === 'jivosite' || this.form.source === 'whatsapp'
          )
        })
      },
      name: { required },
      language: { required },
      email: { email },
      phone: {},
      facebook: { url },
      instagram: { url },
    },
    direct_form: {
      name: { required },
      source: { required },
      language: { required },
      created_at: { required },
      direct_request_info: { required },
      preferred_communication_method: { required, supported: value => !value || ['whatsapp', 'email'].includes(value) },
      email: {
        email,
        required: requiredIf(function () { return this.direct_form.preferred_communication_method === 'email' })
      },
      phone: {
        valid: value => value !== 0,
        required: requiredIf(function () { return this.direct_form.preferred_communication_method === 'whatsapp' })
      },
      facebook: { url },
      instagram: { url },
    },
    reason_form: {
      status_reason: { required },
    },
    next_contact_form: {
      next_contact: {
        required,
        minValue(val) {
          return val ? new Date(val) >= new Date(new Date().setHours(0, 0, 0, 0)) : true
        }
      },
    },
    date_form: {
      methods: { required },
      date: { required },
      report: {
        required: requiredIf(function () {
          return this.report_required
        })
      },
    },
    follow_form: {
      template: { required },
    },
  },
  created() {
    if (this.is_general_manager) this.table.columns.push('manager')
    if (this.is_general_manager || this.is_sales_supervisor) this.fetchItemsNew()
    if (this.is_sales_manager) this.setupLongPolling()
    this.fetchItemsNewDouble()
    this.fetchAnalyticsData()
    getCountries().then(response => { this.country_options = response.data })
    getClientsTourOperators().then(res => { this.tour_operators_options = res.data })
    getLeadsSources().then(res => { this.source_options = res.data })
    getLeadsStatuses().then(res => {
      this.status_options = res.data
      this.form.status = res.data[0].code
    })
    getLeadsAdvSources().then(res => { this.advertising_source_options = res.data })
    getLanguages().then(res => {
      this.languages_options = res.data
      this.form.language = res.data[0].code
      this.direct_form.language = res.data[0].code
    })
    getUsers({ group: 'sales_manager', is_active: true }).then(response => { this.managers_options = response.data })
    getLeadsCommunicationMethods().then(response => { this.communication_options = response.data })
  },
  methods: {
    guidanceRows() {
      return this.table.items.concat(this.table_new.items, this.table_new_double.items)
    },
    newLeadRequestDetails(row) {
      return [
        { label: 'Climb type', value: row.climb_type },
        { label: 'Group type', value: row.group_type },
        { label: 'Hotel Stars', value: row.hotel_stars },
        { label: 'Safari Duration', value: row.safari_duration },
        { label: 'Kilimanjaro Duration', value: row.kilimanjaro_duration },
        { label: 'Trip Price', value: row.trip_price },
        { label: 'Adults', value: row.adults },
        { label: 'Teenagers', value: row.teenagers },
        { label: 'Children', value: row.children },
        { label: 'Request', value: row.request_info?.request },
      ].filter(detail => detail.value !== null && detail.value !== undefined && detail.value !== '')
    },
    setupLongPolling() {
      const pollInterval = 60000
      const poll = () => {
        getLeadsUnassigned()
          .then(res => {
            this.lead_count = res.data.total_count
            setTimeout(poll, pollInterval)
          })
      }
      poll()
    },
    onAssignLead() {
      this.$refs.assign_lead.preload = true
      postLeadsAssigned()
        .then(res => { this.$router.push({ name: 'leads-detail-lead', params: { id: res.data.id } }) })
        .catch(() => { this.setupLongPolling() })
    },
    onTicketSearch(search, loading) {
      loading(true)
      this.searchTicket(loading, search, this)
    },
    searchTicket: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.trengo_ticket_options = []
      } else {
        getLeadsTrengoTickets({ search }).then(response => {
          vm.trengo_ticket_options = response.data
          loading(false)
        })
      }
    }, 350),
    onLeadSourceChanged() {
      if (this.form.source && (
        this.form.source !== 'jivosite' || this.form.source !== 'whatsapp'
      ) && this.form.trengo_ticket) this.form.trengo_ticket = null
    },
    timezoneLabel(timezone) {
      return timezone.label || timezone.timezone_name
    },
    onCountryChange(formName) {
      const form = this[formName]
      const optionsName = formName === 'form' ? 'timezone_options' : 'direct_timezone_options'

      form.country_timezone = null
      this[optionsName] = []
      this.country_cleared[formName] = !form.country
      if (!form.country) return

      const country = form.country
      getCountryTimezones(country).then(response => {
        if (form.country === country) this[optionsName] = response.data
      })
    },
    calculateTimeDiff(start) {
      const difference = differenceInMinutes(new Date(), new Date(start))
      const hours = Math.trunc(difference / 60)
      const minutes = difference % 60

      return {
        time_string: `${ hours ? `${ hours }h ` : '' }${ minutes }m`,
        difference
      }
    },
    onShowExportModal() {
      this.$modal.show('export-modal')
    },
    onSendExport() {
      this.$refs.export_submit.preload = true
      getLeadExport({
        climbing: this.export_form.climbing,
        day_trip: this.export_form.day_trip,
        island: this.export_form.island,
        safari: this.export_form.safari,
        status: this.export_form.status,
        status_exclude: this.export_form.status_exclude,
        last_contact_after: this.export_form.last_contact ? this.export_form.last_contact[0] : '',
        last_contact_before: this.export_form.last_contact ? this.export_form.last_contact[1] : '',
        created_at_after: this.export_form.created_at ? this.export_form.created_at[0] : '',
        created_at_before: this.export_form.created_at ? this.export_form.created_at[1] : '',
      })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `Lead’s export ${ format(new Date(), 'dd.MM.yyyy HH_mm') }.xlsx`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
          this.$refs.export_submit.preload = false
          this.export_form = {}
          this.$modal.hide('export-modal')
        })
        .catch(() => { this.$refs.export_submit.preload = false })
    },
    fetchFollowUpTemplates() {
      getLeadFollowUpTemplates({
        language: this.follow_language,
        available_statuses: [this.follow_status],
        climbing: this.follow_filters.climbing || '',
        day_trip: this.follow_filters.day_trip || '',
        island: this.follow_filters.island || '',
        safari: this.follow_filters.safari || '',
      }).then(response => {
        this.templates_options = response.data
      })
    },
    onShowFollowModal(row) {
      this.follow_filters = {
        climbing: row.climbing,
        day_trip: row.day_trip,
        island: row.island,
        safari: row.safari,
      }
      this.follow_language = row.language.code
      this.follow_status = row.status.code
      this.follow_id = row.id
      this.fetchFollowUpTemplates()
      this.follow_form.template = null
      this.$v.follow_form.$reset()
      this.$modal.show('follow-modal')
    },
    onChangeFilterFollow() {
      this.fetchFollowUpTemplates()
    },
    onCheckFollowForm() {
      this.$v.follow_form.$touch()
      if (this.$v.follow_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFollowForm()
      }
    },
    sendFollowForm() {
      this.$refs.follow_submit.preload = true
      this.table_preloader = true
      postLeadFollowUp(this.follow_id, { template: this.follow_form.template })
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Follow up created',
          })
          this.$modal.hide('follow-modal')
        })
        .catch(() => {
          this.table_preloader = false
          this.$refs.follow_submit.preload = false
        })
    },
    onOptionSelected(row) {
      if (row.status.reason_required) {
        getLeadsStatusReason({ status: row.status.code, active: true })
          .then(response => { this.reason_options = response.data })
        this.reason_form.status = row.status.code
        this.reason_row_updated = row.id
        this.onShowModalReason()
        return
      }
      if (row.status.next_contact) {
        this.next_contact_form.status = row.status.code
        this.reason_row_updated = row.id
        this.onShowModalNextContact()
        return
      }
      this.table_preloader = true
      patchLead(row.id, { status: row.status.code })
        .then(() => {
          this.fetchItems()
        })
        .catch(() => {
          this.table_preloader = false
        })
    },
    onInputPicker() {
      if (!this.$refs.date_picker.pickerOpen) this.fetchItems()
    },
    onInputPickerRequest() {
      if (!this.$refs.date_picker_request.pickerOpen) this.fetchItemsRequest()
    },
    onInputPickerConfirmation() {
      if (!this.$refs.date_picker_confirmation.pickerOpen) this.fetchItemsRequest()
    },
    fetchItemsRequest() {
      this.fetchAnalyticsData()
    },
    fetchItems() {
      getLeads({
        page: this.pagination.page,
        page_size: 20,
        manager_assigned: true,
        search: this.search,
        manager: this.search ? '' : this.manager,
        status: this.search || ['follow_up', 'double_request', 'no_contact', 'incomplete_leads'].includes(this.status_filter) ? '' : this.status_filter,
        climbing: !this.search && this.filter_climbing ? this.filter_climbing : '',
        safari: !this.search && this.filter_safari ? this.filter_safari : '',
        day_trip: !this.search && this.filter_day_trip ? this.filter_day_trip : '',
        island: !this.search && this.filter_island ? this.filter_island : '',
        travel_time__gte: this.search ? '' : this.filter_dates?.start,
        travel_time__lte: this.search ? '' : this.filter_dates?.end,
        created_from: this.search ? '' : this.filter_dates_request?.start,
        created_to: this.search ? '' : this.filter_dates_request?.end,
        confirmation_from: this.search ? '' : this.filter_dates_confirmation?.start,
        confirmation_to: this.search ? '' : this.filter_dates_confirmation?.end,
        need_follow_up: this.search || ['double_request', 'no_contact', 'incomplete_leads'].includes(this.status_filter) ? '' : this.status_filter === 'follow_up',
        is_double_request: this.search || this.status_filter !== 'double_request' ? '' : true,
        no_contact: this.search || this.status_filter !== 'no_contact' ? '' : true,
        incomplete_leads: this.search || !['incomplete_leads', 'deposit_paid', 'full_paid'].includes(this.status_filter) ? '' : !(
          this.status_filter === 'deposit_paid' || this.status_filter === 'full_paid'
        ),
        adv_source: this.search ? '' : this.adv_source_filter?.code,
        adv_campaign: this.search ? '' : this.adv_campaign_filter,
        ordering: this.search ? '' : ['lost', 'refused'].includes(this.status_filter) ? '-created_at' : '',
        language: this.search ? '' : this.language_filter,
        my: this.is_sales_manager ? this.filter_my_lead : '',
        tour_operator: this.tour_operator_filter,
      })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.table_preloader = false
        })
    },
    fetchItemsNew() {
      getLeads({
        page: this.pagination_new.page,
        page_size: 10,
        manager_assigned: false,
        status_exclude: 'spam,double_request,lead_magnet',
        is_double_request: false
      })
        .then(response => {
          this.table_new.items = response.data.results
          this.pagination_new.pages = response.data.pages
          this.pagination_new.count = response.data.count
          this.table_new_preloader = false
        })
    },
    fetchItemsNewDouble() {
      getLeads({
        page: this.pagination_new_double.page,
        page_size: 10,
        manager_assigned: false,
        status_exclude: 'spam,double_request,lead_magnet',
        is_double_request: true
      })
        .then(response => {
          this.table_new_double.items = response.data.results
          this.pagination_new_double.pages = response.data.pages
          this.pagination_new_double.count = response.data.count
          this.table_new_double_preloader = false
        })
    },
    fetchAnalyticsData() {
      getLeadsAnalytics({
        manager: this.manager,
        created_from: this.filter_dates_request?.start,
        created_to: this.filter_dates_request?.end,
        confirmation_from: this.filter_dates_confirmation?.start,
        confirmation_to: this.filter_dates_confirmation?.end,
        source: this.search ? '' : this.adv_source_filter?.code,
        campaign: this.search ? '' : this.adv_campaign_filter,
        language: this.search ? '' : this.language_filter
      })
        .then(res => {
          this.fetchItems()
          this.analytics_data = res.data
          this.status_filters1[0].label = `Lead (${ res.data.statuses.lead.all } / ${ res.data.statuses.lead.expired })`
          this.status_filters1[1].label = `Negotiation (${ res.data.statuses.negotiation.all } / ${ res.data.statuses.negotiation.expired })`
          this.status_filters1[2].label = `Nurture (${ res.data.statuses.nurture.all } / ${ res.data.statuses.nurture.expired })`
          this.status_filters2[0].label = `Pro forma (${ res.data.statuses.pro_forma.all } / ${ res.data.statuses.pro_forma.expired })`
          this.status_filters3[0].label = `NR 1 (${ res.data.statuses.nr1.all } / ${ res.data.statuses.nr1.expired })`
          this.status_filters3[1].label = `NR 2 (${ res.data.statuses.nr2.all } / ${ res.data.statuses.nr2.expired })`
          this.status_filters4[0].label = `Postponed (${ res.data.statuses.postponed.all } / ${ res.data.statuses.postponed.expired })`
          this.status_filters5[2].label = `All (${ res.data.all })`
          this.status_filters6[0].label = `Need follow up (${ res.data.expired })`
          this.status_filters6[1].label = `No contact (${ res.data.no_contact })`
          this.status_filters6[2].label = `Double request (${ res.data.double_requests })`
          this.status_filters2[1].label = `Deposit paid (${ res.data.statuses.deposit_paid.all })`
          this.status_filters2[2].label = `Full paid (${ res.data.statuses.full_paid.all })`
          this.status_filters6[1].show = !!res.data.no_contact
          this.status_filters6[2].show = !!res.data.double_requests
          const current_filter = this.status_filters6.filter(item => item.value === this.status_filter)
          if (current_filter.length && !current_filter[0].show) this.status_filter = 'follow_up'
          this.is_loaded = true
        })
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    onClickPaginationNew(page) {
      this.table_new_preloader = true
      this.pagination_new.page = page
      this.fetchItemsNew()
    },
    onClickPaginationNewDouble(page) {
      this.table_new_double_preloader = true
      this.pagination_new_double.page = page
      this.fetchItemsNewDouble()
    },
    onInputAdvertisingSource(e) {
      this.form.adv_campaign = null
      if (e) this.advertising_campaign_options = e.campaigns
    },
    onInputAdvertisingSourceFilter(e) {
      this.adv_campaign_filter = null
      this.adv_campaign_options_filter = e ? e.campaigns : []
      this.fetchItemsRequest()
    },
    onShowDateModal(id) {
      this.date_row_id = id
      this.date_form.date = formatISO(new Date())
      this.date_form.report = null
      this.date_form.methods = []
      this.$v.date_form.$reset()
      this.$modal.show('date-modal')
    },
    onCheckDateForm() {
      this.$v.date_form.$touch()
      if (this.$v.date_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendDateForm()
      }
    },
    sendDateForm() {
      this.$refs.date_submit.preload = true
      this.table_preloader = true
      const data = { ...this.date_form }
      data.methods = data.methods.map(item => item.code)
      postLeadCommunication(this.date_row_id, data)
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Communication created',
          })
          this.$modal.hide('date-modal')
        })
        .catch(() => {
          this.table_preloader = false
          this.$refs.date_submit.preload = false
        })
    },
    onCheckReasonForm() {
      this.$v.reason_form.$touch()
      if (this.$v.reason_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendReasonForm()
      }
    },
    sendReasonForm() {
      this.$refs.reason_submit.preload = true
      this.table_preloader = true
      patchLead(this.reason_row_updated, this.reason_form)
        .then(() => {
          this.fetchAnalyticsData()
          this.$notify({
            type: 'success',
            title: 'Status changed',
          })
          this.$modal.hide('reason-modal')
        })
        .catch(() => {
          this.$refs.reason_submit.preload = false
        })
    },
    onCheckNextContactForm() {
      this.$v.next_contact_form.$touch()
      if (this.$v.next_contact_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendNextContactForm()
      }
    },
    sendNextContactForm() {
      this.$refs.next_contact_submit.preload = true
      this.table_preloader = true
      patchLead(this.reason_row_updated, this.next_contact_form)
        .then(() => {
          this.fetchAnalyticsData()
          this.$notify({
            type: 'success',
            title: 'Status changed',
          })
          this.$modal.hide('next-contact-modal')
        })
        .catch(() => {
          this.$refs.next_contact_submit.preload = false
          this.table_preloader = false
        })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      postLead(this.normalizeForm(this.form, this.country_cleared.form))
        .then(response => {
          this.$router.push({ name: 'leads-detail-lead', params: { id: response.data.id } })
        })
        .catch(() => {
          this.$refs.submit.preload = false
        })
    },
    onCheckFormDirect() {
      this.$v.direct_form.$touch()
      if (this.$v.direct_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFormDirect()
      }
    },
    sendFormDirect() {
      this.$refs.submit_direct.preload = true
      postDirectLead(this.normalizeForm(this.direct_form, this.country_cleared.direct_form))
        .then(() => {
          this.fetchItemsNew()
          this.$notify({
            type: 'success',
            title: 'Lead created',
          })
          this.onHideCreateDirectModal()
          this.direct_form = {}
        })
        .catch(() => {
          this.$refs.submit_direct.preload = false
        })
    },
    normalizeForm(targetForm, countryCleared = false) {
      const form = {}
      for (const property in targetForm) {
        if (targetForm[property]) form[property] = targetForm[property]
      }
      if (countryCleared) form.country = null
      if (form.trengo_ticket) form.trengo_ticket = form.trengo_ticket.id
      if (form.adv_source) form.adv_source = form.adv_source.code
      return form
    },
    onLeadsSearch() {
      this.searchLeads(this)
    },
    searchLeads: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    onChangeFilter(e) {
      this.search = ''
      this.pagination.page = 1
      if (e === 'manager_select') this.fetchAnalyticsData()
      else this.fetchItems()
    },
    onShowModalReason() {
      this.reason_form.status_reason = null
      this.reason_form.status_reason_note = null
      this.$v.reason_form.$reset()
      this.$modal.show('reason-modal')
    },
    onShowModalNextContact() {
      this.next_contact_form.next_contact = null
      this.$v.next_contact_form.$reset()
      this.$modal.show('next-contact-modal')
    },
    onShowCreateModal() {
      this.$modal.show('leads-modal')
    },
    onHideCreateModal() {
      this.$modal.hide('leads-modal')
    },
    onShowCreateDirectModal() {
      this.$modal.show('leads-direct-modal')
      if (this.direct_form.country) this.onCountryChange('direct_form')
    },
    onHideCreateDirectModal() {
      this.$modal.hide('leads-direct-modal')
      this.direct_form.country_timezone = null
      this.direct_timezone_options = []
      this.country_cleared.direct_form = false
    },
    onCopyContactLink(link) {
      navigator.clipboard.writeText(link)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    dateFormatMonth(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM yyyy') : '-'
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    dateFormatLastContact(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
  },
}
</script>

<style lang="sass" scoped>
.new-lead-quick-payment--active
  background-color: $color-theme

.new-lead-request-text
  white-space: pre-line
  overflow-wrap: anywhere
</style>

<style lang="sass">
.tooltip .tooltip-inner.new-lead-request-popover-inner
  max-width: 700px
</style>
